import React, { useState } from 'react';
import { ArrowRight, Play, Users, Calendar, Mail, Shield, Building, Award, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

interface EnterpriseHeroSectionProps {
  onDemoClick?: () => void;
  onBookDemo?: () => void;
}

const EnterpriseHeroSection: React.FC<EnterpriseHeroSectionProps> = ({ onDemoClick, onBookDemo }) => {
  const [showVideo, setShowVideo] = useState(false);
  const navigate = useNavigate();

  // Handler for demo signin
  const handleDemoSignIn = () => {
    if (onDemoClick) {
      onDemoClick();
    } else {
      navigate('/login?demo=true');
    }
  };

  // Handler for book demo
  const handleBookDemo = () => {
    if (onBookDemo) {
      onBookDemo();
    } else {
      navigate('/book-demo');
    }
  };

  // Enterprise-focused navigation
  const EnterpriseNav = () => (
    <div className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex justify-between items-center">
        <div className="flex items-center">
          <img 
            src="/favicon.svg" 
            alt="Munger.ai" 
            className="h-8 w-8 mr-2"
          />
          <span className="font-bold text-lg sm:text-xl text-gray-900">Munger.ai</span>
          <Badge className="ml-3 bg-blue-100 text-blue-700 border-blue-200 hidden sm:inline-flex">
            Enterprise
          </Badge>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/login')}
            className="text-gray-600 hover:text-gray-900 text-xs sm:text-sm px-3 sm:px-4"
          >
            Sign In
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleBookDemo}
            className="border-gray-300 hover:border-gray-400 text-xs sm:text-sm px-3 sm:px-4"
          >
            <span className="hidden sm:inline">Schedule </span>Demo
          </Button>
          <Button 
            size="sm" 
            className="bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm px-3 sm:px-4" 
            onClick={handleDemoSignIn}
          >
            Try Demo<span className="hidden sm:inline"> Account</span>
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative overflow-hidden bg-gradient-to-b from-white to-gray-50">
      {/* Enterprise Navigation */}
      <EnterpriseNav />

      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-24 pb-20">
        <div className="text-center">
          {/* Enterprise trust signals */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-8"
          >
            <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-xs sm:text-sm">
              <Building className="w-3 h-3 mr-1" />
              Fortune 500 Trusted
            </Badge>
            <Badge className="bg-purple-50 text-purple-700 border-purple-200 text-xs sm:text-sm">
              <Shield className="w-3 h-3 mr-1" />
              SOC 2 Certified
            </Badge>
            <Badge className="bg-green-50 text-green-700 border-green-200 text-xs sm:text-sm">
              <Award className="w-3 h-3 mr-1" />
              99.9% Uptime SLA
            </Badge>
          </motion.div>

          {/* Enterprise announcement */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-block mb-8"
          >
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-full text-sm font-medium shadow-lg">
              🏢 Enterprise Solution – See How Industry Leaders Scale Their Outreach
            </div>
          </motion.div>

          {/* Main content wrapper */}
          <div className="relative">
            {/* Enterprise value proposition */}
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl sm:text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight px-4"
            >
              Enterprise AI Outreach at Scale
            </motion.h1>

            {/* Supporting copy - enterprise benefits */}
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-lg sm:text-xl md:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto px-4"
            >
              Transform your sales pipeline with AI that finds, engages, and converts 
              enterprise prospects automatically. Trusted by 2,100+ companies worldwide.
            </motion.p>

            {/* Key metrics */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-12"
            >
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                <div className="text-2xl font-bold text-blue-600">250M+</div>
                <div className="text-sm text-gray-600">Verified Contacts</div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                <div className="text-2xl font-bold text-purple-600">70+</div>
                <div className="text-sm text-gray-600">Warmed Domains</div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                <div className="text-2xl font-bold text-green-600">7,200+</div>
                <div className="text-sm text-gray-600">Meetings Booked</div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                <div className="text-2xl font-bold text-orange-600">10x</div>
                <div className="text-sm text-gray-600">ROI Average</div>
              </div>
            </motion.div>

            {/* Hero video with play button overlay */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="relative max-w-4xl mx-auto mb-12"
            >
              {!showVideo ? (
                <div 
                  className="relative cursor-pointer group"
                  onClick={() => setShowVideo(true)}
                >
                  <div className="aspect-video bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl shadow-2xl overflow-hidden">
                    {/* Video thumbnail */}
                    <img 
                      src="/lovable-uploads/18b8a76c-9007-4fbf-b3e0-cc92b07d922e.png" 
                      alt="Munger.ai Enterprise Demo"
                      className="w-full h-full object-cover"
                    />
                    
                    {/* Play button overlay */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                      <div className="bg-white rounded-full p-6 shadow-xl group-hover:scale-110 transition-transform">
                        <Play className="w-8 h-8 text-blue-600 ml-1" fill="currentColor" />
                      </div>
                    </div>
                  </div>
                  
                  <p className="mt-4 text-sm text-gray-600">
                    Watch 2-minute demo: See how enterprises book 50+ meetings monthly
                  </p>
                </div>
              ) : (
                <div className="aspect-video bg-black rounded-2xl shadow-2xl overflow-hidden">
                  <iframe
                    src="https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              )}
            </motion.div>

            {/* Primary CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8"
            >
              <Button 
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 px-8 py-4 text-lg font-semibold"
                onClick={handleDemoSignIn}
              >
                Access Demo Account
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              
              <Button 
                variant="outline" 
                size="lg"
                className="border-gray-300 hover:border-gray-400 px-8 py-4 text-lg font-semibold"
                onClick={handleBookDemo}
              >
                Schedule Enterprise Demo
              </Button>
            </motion.div>

            {/* No pricing mention - focus on value */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-600"
            >
              <span className="flex items-center gap-1">
                <Zap className="w-4 h-4 text-yellow-500" />
                Setup in 10 minutes
              </span>
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4 text-blue-500" />
                Unlimited team members
              </span>
              <span className="flex items-center gap-1">
                <Shield className="w-4 h-4 text-green-500" />
                Enterprise security
              </span>
            </motion.div>
          </div>
        </div>

        {/* Social proof logos */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mt-20"
        >
          <p className="text-center text-sm text-gray-500 mb-8">
            TRUSTED BY LEADING ENTERPRISES
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 opacity-60 grayscale">
            {/* Placeholder for company logos */}
            <div className="h-10 w-32 bg-gray-300 rounded"></div>
            <div className="h-10 w-32 bg-gray-300 rounded"></div>
            <div className="h-10 w-32 bg-gray-300 rounded"></div>
            <div className="h-10 w-32 bg-gray-300 rounded"></div>
            <div className="h-10 w-32 bg-gray-300 rounded"></div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default EnterpriseHeroSection;